<template>
  <div class="container-fluid py-4">
    <!-- Bulk Actions -->
    <div class="mb-3 text-end">
      <button class="btn btn-success me-2" :disabled="selected.length === 0 || loading || isProcessing"
        @click="confirmSelected">
        <span v-if="isProcessing && actionType === 'approve'" class="spinner-border spinner-border-sm me-2"
          role="status" aria-hidden="true"></span>
        <i v-else class="material-icons text-sm me-1">check_circle</i>
        {{ isProcessing && actionType === 'approve' ? 'Confirming...' : `Confirm (${selected.length})` }}
      </button>
      <button class="btn btn-danger" :disabled="selected.length === 0 || loading || isProcessing"
        @click="declineSelected">
        <span v-if="isProcessing && actionType === 'decline'" class="spinner-border spinner-border-sm me-2"
          role="status" aria-hidden="true"></span>
        <i v-else class="material-icons text-sm me-1">cancel</i>
        {{ isProcessing && actionType === 'decline' ? 'Declining...' : `Decline (${selected.length})` }}
      </button>
    </div>
    <!-- Table -->
    <BaseTable :rows="registrations" :columns="columns" :selected="selected" selectable searchable :loading="loading"
      :pagination="pagination" @update:selected="handleSelectionChange" @changePage="onChangePage"
      @updatePageSize="onUpdatePageSize" @searchChanged="onSearchChanged">
      <!-- Custom column -->
      <template #col-registration_reason="{ row }">
        <div class="p-2 rounded small">
          {{ row.registration_reason }}
        </div>
      </template>
      <template #col-registration_date="{ row }">
        <div class="p-2 rounded small">
          {{ row.registration_date }}
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import BaseTable from "@/components/Table/Table.vue";
import { getLocationId } from "../../utils/SessionDataHandler";
import { useToast } from "vue-toast-notification";

const store = useStore();
const toast = useToast({
  position: "top-right",
  duration: 3000,
  dismissible: true,
  pauseOnHover: true,
  queue: true,
});

const locationId = getLocationId();
const selected = ref([]);
const searchTerm = ref("");
const isProcessing = ref(false);
const actionType = ref(null); // 'approve' or 'decline'

// 🔹 Vuex State
const registrations = computed(() => store.getters["pendingUsers"]);
const pagination = computed(() => store.getters["pendingPagination"]);
const loading = ref(false);

// 🔹 Columns
const columns = [
  { key: "firstname", label: "First Name" },
  { key: "lastname", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "registration_reason", label: "Reason" },
  { key: "registration_date", label: "Date" }
];

// 🔹 Fetch Data
const fetchPendingUsers = async (page = 1, pageSize = pagination.value.pageSize || 10) => {
  loading.value = true;
  try {
    await store.dispatch("getPendingUsers", {
      locationId,
      pageNumber: page,
      pageSize,
    });
  } catch (err) {
    console.error("Error fetching users:", err);
    toast.error(err.message || "Failed to fetch pending users");
  } finally {
    loading.value = false;
  }
};

// Lifecycle
onMounted(() => {
  fetchPendingUsers();
});

// 🔹 Handle Selection Change - Fixed for proper reactivity
const handleSelectionChange = (newSelection) => {
  // Ensure we're creating a new array reference for reactivity
  selected.value = Array.isArray(newSelection) ? [...newSelection] : [];
};

// 🔹 Pagination + Search
const onChangePage = async (page) => {
  await fetchPendingUsers(page);
};

const onUpdatePageSize = async (size) => {
  await fetchPendingUsers(1, size);
};

const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchPendingUsers(1);
};

// 🔹 Confirm Selected (Approve)
const confirmSelected = async () => {
  if (selected.value.length === 0 || isProcessing.value) return;

  isProcessing.value = true;
  actionType.value = 'approve';

  try {
    await store.dispatch("approveUsers", {
      locationId,
      userIds: selected.value,
    });

    toast.success("Selected users approved successfully");

    // Clear selection after successful action
    selected.value = [];

    // Refresh the current page
    await fetchPendingUsers(pagination.value.currentPage || 1);
  } catch (err) {
    console.error("Error approving users:", err);
    toast.error(err.message || "Failed to approve selected users");
  } finally {
    isProcessing.value = false;
    actionType.value = null;
  }
};

// 🔹 Decline Selected (Deny)
const declineSelected = async () => {
  if (selected.value.length === 0 || isProcessing.value) return;

  isProcessing.value = true;
  actionType.value = 'decline';

  try {
    await store.dispatch("denyUsers", {
      locationId,
      userIds: selected.value,
    });

    toast.info("Selected users declined");

    // Clear selection after successful action
    selected.value = [];

    // Refresh the current page
    await fetchPendingUsers(pagination.value.currentPage || 1);
  } catch (err) {
    console.error("Error denying users:", err);
    toast.error(err.message || "Failed to decline selected users");
  } finally {
    isProcessing.value = false;
    actionType.value = null;
  }
};
</script>

<style>
.v-toast {
  z-index: 9999 !important;
}
</style>